// Draws every emoji in the editor with Apple's images, so it looks the same
// as on a Mac on any OS (flags included — Windows has none of its own).
//
// The images come from the emoji-datasource-apple package on jsdelivr.
//
// What counts as an emoji follows macOS: a character with emoji presentation
// by default, or one followed by U+FE0F, or any astral pictograph. A plain
// BMP symbol like ✓ ⚠ ★ ♪ stays text, keeping its CSS colour.
//
// React owns its text nodes and keeps writing into them, so a text node is
// never replaced: it is emptied and a sibling <span> right after it carries
// the drawn copy. When React writes new text into the node, the copy is
// rebuilt. Anything being edited (inputs, contentEditable) is left alone,
// and a field that turns editable gets its original text back first, since
// the editors read innerText on save.

export const APPLE_EMOJI_CDN =
  'https://cdn.jsdelivr.net/npm/emoji-datasource-apple@16.0.0/img/apple/64/';

const EMOJI_RE = new RegExp(
  [
    '\\p{Regional_Indicator}\\p{Regional_Indicator}',
    '\\u{1F3F4}[\\u{E0061}-\\u{E007A}]+\\u{E007F}',
    '[#*0-9]\\uFE0F?\\u20E3',
    '(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F|(?![\\u0000-\\uFFFF])\\p{Extended_Pictographic})' +
      '\\p{Emoji_Modifier}?' +
      '(?:\\u200D\\p{Extended_Pictographic}\\uFE0F?\\p{Emoji_Modifier}?)*',
  ].join('|'),
  'gu'
);

const SKIP_SELECTOR = 'script,style,textarea,input,select,option,title,svg,[data-no-apple-emoji]';
const COPY_CLASS = 'apple-emoji-text';

// seq -> file name that loaded, or null when the set has no such image
const resolved = new Map();
// emptied text node -> { text, copy }
const tracked = new WeakMap();

const hex = (seq) => [...seq].map((c) => c.codePointAt(0).toString(16).padStart(4, '0'));

// The set names files by the fully-qualified sequence; text often drops or
// adds U+FE0F, so try the sequence as written, then without and with it.
const candidates = (seq) => {
  const cps = hex(seq);
  const bare = cps.filter((c) => c !== 'fe0f');
  const list = [cps.join('-'), bare.join('-')];
  if (bare.length) list.push([bare[0], 'fe0f', ...bare.slice(1)].join('-'));
  return [...new Set(list)];
};

const makeImg = (seq) => {
  const known = resolved.get(seq);
  if (known === null) return document.createTextNode(seq);
  const tries = known ? [known] : candidates(seq);
  let i = 0;
  const img = document.createElement('img');
  img.className = 'apple-emoji';
  img.alt = seq;
  img.draggable = false;
  img.onload = () => resolved.set(seq, tries[i]);
  img.onerror = () => {
    i += 1;
    if (i < tries.length) {
      img.src = APPLE_EMOJI_CDN + tries[i] + '.png';
    } else {
      resolved.set(seq, null);
      img.replaceWith(document.createTextNode(seq));
    }
  };
  img.src = APPLE_EMOJI_CDN + tries[0] + '.png';
  return img;
};

const isCopy = (node) => {
  const el = node.nodeType === 1 ? node : node.parentElement;
  return !!(el && el.closest('.' + COPY_CLASS));
};

const skipped = (textNode) => {
  const el = textNode.parentElement;
  return !el || el.isContentEditable || !!el.closest(SKIP_SELECTOR) || isCopy(textNode);
};

const dropCopy = (node) => {
  const rec = tracked.get(node);
  if (!rec) return;
  rec.copy.remove();
  tracked.delete(node);
};

// Give a tracked node its own text back (before it becomes editable).
const restore = (node) => {
  const rec = tracked.get(node);
  if (!rec) return;
  dropCopy(node);
  node.data = rec.text;
};

const drawText = (node) => {
  dropCopy(node);
  if (skipped(node)) return;
  const text = node.data;
  // matchAll starts from the regex's lastIndex, so never .test() it first
  EMOJI_RE.lastIndex = 0;
  const matches = [...text.matchAll(EMOJI_RE)];
  if (!matches.length) return;

  const copy = document.createElement('span');
  copy.className = COPY_CLASS;
  let last = 0;
  for (const m of matches) {
    if (m.index > last) copy.appendChild(document.createTextNode(text.slice(last, m.index)));
    copy.appendChild(makeImg(m[0]));
    last = m.index + m[0].length;
  }
  if (last < text.length) copy.appendChild(document.createTextNode(text.slice(last)));

  node.parentNode.insertBefore(copy, node.nextSibling);
  tracked.set(node, { text, copy });
  node.data = '';
};

const textNodesIn = (root) => {
  if (root.nodeType === 3) return [root];
  if (root.nodeType !== 1) return [];
  const out = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) out.push(walker.currentNode);
  return out;
};

const drawTree = (root) => {
  if (isCopy(root)) return;
  textNodesIn(root).forEach((n) => {
    if (!tracked.has(n) && n.data) drawText(n);
  });
};

const onMutations = (records) => {
  for (const r of records) {
    if (r.type === 'characterData') {
      const node = r.target;
      if (isCopy(node)) continue;
      if (tracked.has(node)) {
        // Our own emptying has a non-empty old value; anything else is React.
        if (node.data === '' && r.oldValue !== '') continue;
        if (node.data === '') { dropCopy(node); continue; }
      }
      drawText(node);
    } else if (r.type === 'childList') {
      r.removedNodes.forEach((n) => {
        if (n.nodeType === 3) dropCopy(n);
      });
      r.addedNodes.forEach((n) => {
        if (n.isConnected) drawTree(n);
      });
    } else if (r.type === 'attributes') {
      const el = r.target;
      if (el.isContentEditable) textNodesIn(el).forEach(restore);
      else drawTree(el);
    }
  }
};

export function startAppleEmoji(root = document.body) {
  drawTree(root);
  new MutationObserver(onMutations).observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
    characterDataOldValue: true,
    attributes: true,
    attributeFilter: ['contenteditable'],
  });
}
